import React, { useState, useEffect, useRef } from "react";
import {
  GoogleMap,
  LoadScript,
  Polygon,
  DrawingManager,
} from "@react-google-maps/api";
import ZoneService from "../services/externalServices/zoneServices";
import DeleteIcon from "@mui/icons-material/Delete";
import { Marker } from "@react-google-maps/api";

const GOOGLE_MAP_API_KEY = "AIzaSyB4ErDCIgl8ahjk_tmEQ_H3oViYGy80trU";

const containerStyle = {
  width: "100%",
  height: "500px",
};

const ZoneMap = () => {
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [editedZones, setEditedZones] = useState({});
  const polygonRefs = useRef({});
  const [geocoder, setGeocoder] = useState(null);
  const [newZonePath, setNewZonePath] = useState([]);
  const [showNewZoneForm, setShowNewZoneForm] = useState(false);
  const [viewMode, setViewMode] = useState("list"); // 'list' or 'map'
  const [drawingMode, setDrawingMode] = useState(false);
  const [editingPoints, setEditingPoints] = useState([]);
  const [tempPolygon, setTempPolygon] = useState(null);
  const [drawingManager, setDrawingManager] = useState(null);
  const [tempPolygonPath, setTempPolygonPath] = useState([]);
  const [currentCenter, setCurrentCenter] = useState(null);
  const mapRef = useRef(null);
  const [mapCenter, setMapCenter] = useState({ lat: 15.2757, lng: 76.3879 });

  useEffect(() => {
    fetchZones();
  }, []);

  useEffect(() => {
    if (mapLoaded && window.google) {
      setGeocoder(new window.google.maps.Geocoder());
    }
  }, [mapLoaded]);

  useEffect(() => {
    if (selectedZone) {
      const initializePoints = async () => {
        const pointsWithNames = await Promise.all(
          selectedZone.points.map(async (point) => {
            // If the point already has a name, keep it
            if (point.name) return point;

            // Otherwise fetch the reverse geocode name
            const name = await reverseGeocode({
              lat: point.latitude,
              lng: point.longitude,
            });

            return {
              ...point,
              name: name || `Point ${point.sequence}`,
              isEnabled: point.isEnabled !== false,
            };
          })
        );
        setEditingPoints(pointsWithNames);
      };

      initializePoints();
    }
  }, [selectedZone]);
  useEffect(() => {
    return () => {
      if (tempPolygon) {
        tempPolygon.setMap(null);
      }
    };
  }, [tempPolygon]);

  useEffect(() => {
    if (selectedZone?.path?.length && !mapRef.current) {
      setMapCenter({
        lat: selectedZone.path[0].lat,
        lng: selectedZone.path[0].lng,
      });
    }
  }, [selectedZone]);

  // const getZoneCenter = (paths) => {
  //   if (!paths || paths.length === 0) return null;

  //   // Simple average of all points
  //   let lat = 0,
  //     lng = 0;
  //   paths.forEach((point) => {
  //     lat += point.lat;
  //     lng += point.lng;
  //   });

  //   return {
  //     lat: lat / paths.length,
  //     lng: lng / paths.length,
  //   };
  // };

  const getZoneCenter = (zone) => {
    if (!zone?.path?.length) return { lat: 15.2757, lng: 76.3879 };

    // If we have a manually set center, use that
    if (currentCenter) return currentCenter;

    // Otherwise calculate centroid of all points
    const centroid = zone.path.reduce(
      (acc, point) => {
        acc.lat += point.lat;
        acc.lng += point.lng;
        return acc;
      },
      { lat: 0, lng: 0 }
    );
    centroid.lat /= zone.path.length;
    centroid.lng /= zone.path.length;

    return centroid;
  };

  const getZoneCenterForMarker = (path) => {
  if (!path || path.length === 0) return { lat: 0, lng: 0 };
  
  // Convert path to LatLng objects if needed
  const points = path.map(p => 
    typeof p.lat === 'function' ? { lat: p.lat(), lng: p.lng() } : p
  );

  // Calculate centroid
  const sum = points.reduce((acc, point) => {
    return {
      lat: acc.lat + point.lat,
      lng: acc.lng + point.lng
    };
  }, { lat: 0, lng: 0 });

  return {
    lat: sum.lat / points.length,
    lng: sum.lng / points.length
  };
};

  const fetchZones = async () => {
    try {
      const res = await ZoneService.getAllZones();
      const formatted = res.map((zone) => ({
        ...zone,
        path: zone.points.map((p) => ({
          lat: p.latitude,
          lng: p.longitude,
        })),
      }));
      setZones(formatted);
    } catch (error) {
      console.error("Error fetching zones:", error);
    }
  };

  const handlePointChange = (index, field, value) => {
    setEditingPoints((prevPoints) => {
      const newPoints = [...prevPoints];
      newPoints[index] = {
        ...newPoints[index],
        [field]: value,
      };
      return newPoints;
    });
  };

  const getname = async (latitude, longitude) => {
    try {
      const latLng = { lat: latitude, lng: longitude };
      const name = await reverseGeocode(latLng);
      return name || `Point ${latitude}, ${longitude}`;
    } catch (err) {
      console.error("Error getting name:", err);
      return `Point ${latitude}, ${longitude}`;
    }
  };

  const updatePointsFromPolygon = async (zoneId) => {
    const polygon = polygonRefs.current[zoneId];
    if (!polygon) return;
    const path = polygon.getPath().getArray();

    const center = path.reduce(
      (acc, point) => {
        acc.lat += point.lat();
        acc.lng += point.lng();
        return acc;
      },
      { lat: 0, lng: 0 }
    );
    center.lat /= path.length;
    center.lng /= path.length;

    setCurrentCenter(center);
    const updatedPointsPromises = path.map(async (latLng, index) => {
      const existingPoint = editingPoints[index] || {};

      // Get the name - either use existing or fetch new one
      let name = await getname(latLng.lat(), latLng.lng());
      //   if (existingPoint.name && typeof existingPoint.name === "string") {
      //     name = existingPoint.name; // Keep existing name if it's valid
      //   } else {
      //      // Fetch new name
      //   }

      return {
        ...existingPoint,
        sequence: index + 1,
        latitude: latLng.lat(),
        longitude: latLng.lng(),
        name: name, // This will always be a string
        isEnabled: existingPoint.isEnabled !== false,
      };
    });

    // Wait for all geocoding to complete
    const updatedPoints = await Promise.all(updatedPointsPromises);
    console.log("updatedPoints", updatedPoints);
    setEditingPoints(updatedPoints);
  };

  const saveZone = async (zoneId) => {
    try {
      // First update points from polygon in case there were any drag/drop changes
      updatePointsFromPolygon(zoneId);

      // Use a timeout to ensure state updates before saving
      setTimeout(async () => {
        await ZoneService.updateZone(zoneId, {
          zoneName: selectedZone.zoneName,
          message: selectedZone.message,
          color: selectedZone.color || "#FF0000",
          isEnabled: selectedZone.isEnabled,
          points: editingPoints.map((p) => ({
            sequence: p.sequence,
            name: p.name,
            latitude: p.latitude,
            longitude: p.longitude,
            isEnabled: p.isEnabled,
          })),
        });
        alert("Zone updated successfully!");
        setViewMode("list");
        fetchZones();
      }, 100);
    } catch (err) {
      console.error("Failed to update zone", err);
      alert("Update failed");
    }
  };

  const reverseGeocode = (latLng) =>
    new Promise((resolve) => {
      if (!geocoder) {
        resolve("");
        return;
      }
      geocoder.geocode({ location: latLng }, (results, status) => {
        if (status === "OK" && results[0]) {
          resolve(results[0].formatted_address);
        } else {
          resolve(""); // fallback if no result
        }
      });
    });

  // const handleNewPolygonComplete = async (polygon) => {
  //   const latLngArray = polygon.getPath().getArray();
  //   setShowNewZoneForm(true);
  //   // Store the polygon temporarily
  //   setTempPolygon(polygon);

  //   const path = await Promise.all(
  //     latLngArray.map(async (latLng, index) => {
  //       const name = await reverseGeocode(latLng);
  //       return {
  //         name: name || `Point ${index + 1}`,
  //         sequence: index + 1,
  //         latitude: latLng.lat(),
  //         longitude: latLng.lng(),
  //         isEnabled: true,
  //       };
  //     })
  //   );

  //   setNewZonePath(path);
  //   setDrawingMode(false);
  // };

  const handleNewPolygonComplete = async (polygon) => {
    const path = polygon
      .getPath()
      .getArray()
      .map((latLng, index) => ({
        latitude: latLng.lat(),
        longitude: latLng.lng(),
        sequence: index + 1,
        name: `Point ${index + 1}`,
        isEnabled: true,
      }));

    setNewZonePath(path);
    setTempPolygonPath(polygon.getPath().getArray());
    polygon.setMap(null); // Remove the auto-created polygon
    setShowNewZoneForm(true);
  };
  const handleEditZone = (zone) => {
    console.log("Editing zone:", zone);
    setSelectedZone(zone);
    setViewMode("map");
  };

  // const centerForZone = (zone) => {
  //   if (!zone?.path?.length) return { lat: 15.2757, lng: 76.3879 };
  //   return {
  //     lat: zone.path[0].lat,
  //     lng: zone.path[0].lng,
  //   };
  // };

  const centerForZone = (zone) => {
    if (!zone?.path?.length) return { lat: 15.2757, lng: 76.3879 };

    const latestPoint = zone.path[zone.path.length - 1]; // Get the last point in the path array

    return {
      lat: latestPoint.lat,
      lng: latestPoint.lng,
    };
  };
  const handleCreateNewZone = () => {
    setSelectedZone(null);
    setDrawingMode(true); // Enable drawing mode
    setViewMode("map");
    setShowNewZoneForm(false); // Ensure form is hidden initially
    setTempPolygonPath([]);
  };

  // const updatePolygonFromPoints = (points) => {
  //   if (tempPolygon) {
  //     const path = points.map(
  //       (point) =>
  //         new window.google.maps.LatLng(point.latitude, point.longitude)
  //     );
  //     tempPolygon.setPath(path);
  //   }
  // };

  const updatePolygonFromPoints = (points) => {
    const path = points.map(
      (point) => new window.google.maps.LatLng(point.latitude, point.longitude)
    );
    setTempPolygonPath(path);
  };

  const handleNewZoneSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;

    const zoneData = {
      zoneName: form.zoneName.value,
      message: form.message.value,
      color: form.color.value || "#FF0000",
      movePinTo: {
        latitude: newZonePath[0]?.latitude,
        longitude: newZonePath[0]?.longitude,
      },
      points: newZonePath.map((p) => ({
        name: p.name,
        sequence: p.sequence,
        latitude: p.latitude,
        longitude: p.longitude,
        isEnabled: p.isEnabled,
      })),
      isEnabled: true,
    };

    try {
      await ZoneService.addZone(zoneData);
      alert("Zone created successfully!");
      setShowNewZoneForm(false);
      setTempPolygonPath([]);
      setNewZonePath([]);
      if (tempPolygon) {
        tempPolygon.setMap(null);
        setTempPolygon(null);
      }
      setViewMode("list");
      fetchZones();
    } catch (err) {
      console.error("Error creating zone:", err);
      alert("Failed to create zone");
    }
  };

  const handleDeleteZone = async (zoneId) => {
    if (window.confirm("Are you sure you want to delete this zone?")) {
      try {
        await ZoneService.deleteZone(zoneId);
        window.alert("Zone deleted successfully");
        fetchZones(); // Refresh the list
      } catch (error) {
        console.error("Error deleting zone:", error);
        window.alert("Failed to delete zone");
      }
    }
  };

  return (
    <div className="p-4">
      {viewMode === "list" ? (
        <div>
          <h2 className="text-2xl font-bold mb-4">Zones List</h2>
          <button
            onClick={handleCreateNewZone}
            className="bg-blue-600 text-white px-4 py-2 rounded mb-4"
          >
            Create New Zone
          </button>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {zones.map((zone) => (
              <div
                key={zone._id}
                className="border p-4 rounded shadow relative hover:shadow-md transition-shadow"
              >
                {/* Delete button with MUI DeleteIcon */}
                <button
                  onClick={() => handleDeleteZone(zone._id)}
                  className="absolute top-2 right-2 text-gray-400 hover:text-red-500 transition-colors"
                  title="Delete zone"
                  aria-label="Delete zone"
                >
                  <DeleteIcon fontSize="small" />
                </button>

                <h3 className="font-bold text-lg pr-6">{zone.zoneName}</h3>
                <p className="text-gray-600">{zone.message}</p>
                <div className="mt-2 flex space-x-2">
                  <button
                    onClick={() => handleEditZone(zone)}
                    className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors"
                  >
                    Edit Zone
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <LoadScript
          googleMapsApiKey={GOOGLE_MAP_API_KEY}
          libraries={["drawing", "places"]}
          onError={(error) =>
            console.error("Google Maps LoadScript error:", error)
          }
          onLoad={() => {
            setMapLoaded(true);
            console.log("Google Maps API loaded");
          }}
        >
          <button
            onClick={() => {
              if (drawingManager) drawingManager.setDrawingMode(null);
              if (tempPolygon) {
                tempPolygon.setMap(null);
                setTempPolygon(null);
              }
              setViewMode("list");
              setDrawingMode(false);
              setSelectedZone(null);
              setShowNewZoneForm(false);
            }}
            className="bg-gray-600 text-white px-4 py-2 rounded mb-2"
          >
            ← Back to List
          </button>

          {showNewZoneForm && (
            <div className="p-4 bg-gray-100 mt-4 rounded shadow mb-4">
              <h3 className="text-lg font-bold mb-2">Create New Zone</h3>

              <form onSubmit={handleNewZoneSubmit}>
                <input
                  name="zoneName"
                  placeholder="Zone Name"
                  required
                  className="block mb-2 p-2 border w-full"
                />
                <input
                  name="message"
                  placeholder="Message"
                  required
                  className="block mb-2 p-2 border w-full"
                />
                <input
                  name="color"
                  placeholder="Color"
                  defaultValue="#FF0000"
                  className="block mb-2 p-2 border w-full"
                />
                <div className="mb-4 max-h-60 overflow-y-auto">
                  <h4 className="font-semibold mb-2">Zone Points:</h4>
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-200">
                        <th className="p-2 border">#</th>
                        <th className="p-2 border">Name</th>
                        <th className="p-2 border">Latitude</th>
                        <th className="p-2 border">Longitude</th>
                        <th className="p-2 border">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {newZonePath.map((point, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="p-2 border">{point.sequence}</td>
                          <td className="p-2 border">
                            <input
                              type="text"
                              value={point.name}
                              onChange={(e) => {
                                const updatedPath = [...newZonePath];
                                updatedPath[index].name = e.target.value;
                                setNewZonePath(updatedPath);
                                updatePolygonFromPoints(updatedPath);
                              }}
                              className="w-full p-1 border rounded"
                            />
                          </td>
                          <td className="p-2 border">
                            <input
                              type="number"
                              value={point.latitude}
                              onChange={(e) => {
                                const updatedPath = [...newZonePath];
                                updatedPath[index].latitude = parseFloat(
                                  e.target.value
                                );
                                setNewZonePath(updatedPath);
                                updatePolygonFromPoints(updatedPath);
                              }}
                              step="0.000001"
                              className="w-full p-1 border rounded"
                            />
                          </td>
                          <td className="p-2 border">
                            <input
                              type="number"
                              value={point.longitude}
                              onChange={(e) => {
                                const updatedPath = [...newZonePath];
                                updatedPath[index].longitude = parseFloat(
                                  e.target.value
                                );
                                setNewZonePath(updatedPath);
                                updatePolygonFromPoints(updatedPath);
                              }}
                              step="0.000001"
                              className="w-full p-1 border rounded"
                            />
                          </td>
                          <td className="p-2 border">
                            <button
                              type="button"
                              onClick={() => {
                                const updatedPath = newZonePath.filter(
                                  (_, i) => i !== index
                                );
                                setNewZonePath(
                                  updatedPath.map((p, i) => ({
                                    ...p,
                                    sequence: i + 1,
                                  }))
                                );
                                updatePolygonFromPoints(updatedPath);
                              }}
                              className="text-red-600 hover:text-red-800"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button
                  type="submit"
                  className="bg-green-600 text-white px-4 py-2 rounded"
                >
                  Submit New Zone
                </button>
              </form>
            </div>
          )}

          {/* <GoogleMap
            mapContainerStyle={containerStyle}
            // center={
            //   selectedZone
            //     ? centerForZone(selectedZone)
            //     : { lat: 15.2757, lng: 76.3879 }
            // }
            center={getZoneCenter(selectedZone)}
            zoom={10}
            onLoad={(map) =>{
              setMapLoaded(true);
               mapRef.current = map;
            } }
          > */}
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={mapCenter}
            zoom={10}
            onLoad={(map) => {
              mapRef.current = map;
              setMapLoaded(true);
            }}
            onDragEnd={() => {
              if (mapRef.current) {
                const newCenter = mapRef.current.getCenter();
                setMapCenter({ lat: newCenter.lat(), lng: newCenter.lng() });
              }
            }}
            onZoomChanged={() => {
              if (mapRef.current) {
                const newCenter = mapRef.current.getCenter();
                setMapCenter({ lat: newCenter.lat(), lng: newCenter.lng() });
              }
            }}
          >
            {/* {zones.map((zone) => (
              <>
                <Polygon
                  key={zone._id}
                  paths={zone.path}
                  options={{
                    strokeColor:
                      zone._id === selectedZone?._id
                        ? "#FF0000"
                        :  zone.color|| "#888888",
                    fillColor:
                      zone._id === selectedZone?._id
                        ?"#FF0000" 
                        :  zone.color || "#888888",
                    fillOpacity: zone._id === selectedZone?._id ? 0.5 : 0.2,
                    strokeWeight: zone._id === selectedZone?._id ? 3 : 1,
                    editable: zone._id === selectedZone?._id,
                    zIndex: zone._id === selectedZone?._id ? 1000 : 1,
                  }}
                  onLoad={(polygon) => {
                    polygonRefs.current[zone._id] = polygon;
                  }}
                  onMouseUp={() => {
                    if (zone._id === selectedZone?._id) {
                      updatePointsFromPolygon(zone._id);
                    }
                  }}
                  onDragEnd={() => {
                    if (zone._id === selectedZone?._id) {
                      updatePointsFromPolygon(zone._id);
                    }
                  }}
                />

                <Marker
                  position={getZoneCenter(zone.path)}
                  label={{
                    text: zone.zoneName,
                    color: zone.color || "#000000",
                    fontSize: "12px",
                    fontWeight: "bold",
                    className: "zone-label",
                  }}
                  icon={{
                    url: "data:image/svg+xml;charset=UTF-8;base64,PHN2ZyB3aWR0aD0iMCIgaGVpZ2h0PSIwIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjwvc3ZnPg==",
                    //   labelOrigin: new window.google.maps.Point(0, 0),
                  }}
                />
              </>
            ))} */}

            {zones.map((zone) => (
              <React.Fragment key={zone._id}>
                <Polygon
                  paths={zone.path}
                  options={{
                    strokeColor:
                      zone._id === selectedZone?._id
                        ? "#FF0000"
                        : selectedZone
                        ? "#888888"
                        : zone.color || "#888888",
                    fillColor:
                      zone._id === selectedZone?._id
                        ? "#FF0000"
                        : selectedZone
                        ? "#888888"
                        : zone.color || "#888888",
                    fillOpacity:
                      zone._id === selectedZone?._id
                        ? 0.5
                        : selectedZone
                        ? 0.3
                        : 0.4,
                    strokeWeight:
                      zone._id === selectedZone?._id ? 3 : selectedZone ? 1 : 1,
                    strokeOpacity:
                      zone._id === selectedZone?._id
                        ? 1
                        : selectedZone
                        ? 0.5
                        : 1,
                    editable: zone._id === selectedZone?._id,
                    zIndex: zone._id === selectedZone?._id ? 1000 : 1,
                  }}
                  onLoad={(polygon) => {
                    polygonRefs.current[zone._id] = polygon;
                  }}
                  onMouseUp={() => {
                    if (zone._id === selectedZone?._id) {
                      updatePointsFromPolygon(zone._id);
                    }
                  }}
                  onDragEnd={() => {
                    if (zone._id === selectedZone?._id) {
                      updatePointsFromPolygon(zone._id);
                    }
                  }}
                />

                <Marker
                  position={getZoneCenterForMarker(zone.path)}
                  label={{
                    text: zone.zoneName,
                    color:
                      selectedZone && zone._id !== selectedZone?._id
                        ? "#888888"
                        : zone.color || "#000000",
                    fontSize: "12px",
                    fontWeight: "bold",
                    className: "zone-label",
                  }}
                  icon={{
                    url: "data:image/svg+xml;charset=UTF-8;base64,PHN2ZyB3aWR0aD0iMCIgaGVpZ2h0PSIwIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjwvc3ZnPg==",
                  }}
                />
              </React.Fragment>
            ))}
            {showNewZoneForm && tempPolygonPath.length > 0 && (
              <Polygon
                paths={tempPolygonPath}
                options={{
                  strokeColor: "#00FF00",
                  fillColor: "#00FF00",
                  fillOpacity: 0.3,
                  strokeWeight: 2,
                  editable: true,
                  draggable: true,
                  zIndex: 1000,
                }}
                onMouseUp={() => {
                  const path = tempPolygonPath.map((latLng, index) => ({
                    latitude: latLng.lat(),
                    longitude: latLng.lng(),
                    sequence: index + 1,
                    name: `Point ${index + 1}`,
                    isEnabled: true,
                  }));
                  setNewZonePath(path);
                }}
                onDragEnd={() => {
                  const path = tempPolygonPath.map((latLng, index) => ({
                    latitude: latLng.lat(),
                    longitude: latLng.lng(),
                    sequence: index + 1,
                    name: `Point ${index + 1}`,
                    isEnabled: true,
                  }));
                  setNewZonePath(path);
                }}
              />
            )}
            {/* Show DrawingManager only when in drawing mode and not showing the form */}
            {!selectedZone && !showNewZoneForm && drawingMode && mapLoaded && (
              <DrawingManager
                onLoad={(manager) => {
                  setDrawingManager(manager);
                  manager.setDrawingMode(
                    window.google?.maps.drawing.OverlayType.POLYGON
                  );
                }}
                onPolygonComplete={handleNewPolygonComplete}
                options={{
                  drawingControl: true,
                  drawingControlOptions: {
                    position: window.google?.maps.ControlPosition.TOP_CENTER,
                    //   drawingModes: ["polygon"],
                    drawingModes: [
                      window.google?.maps.drawing.OverlayType.POLYGON,
                    ],
                  },
                  polygonOptions: {
                    fillColor: "#00FF00",
                    fillOpacity: 0.2,
                    strokeWeight: 2,
                    editable: true,
                    draggable: false,
                    zIndex: 1,
                  },
                }}
              />
            )}
          </GoogleMap>

          <div className="mb-4">
            {/* Top Toolbar */}
            {/* <div className="flex justify-between items-center mb-4">
              <button
                onClick={() => {
                  if (drawingManager) drawingManager.setDrawingMode(null);
                  if (tempPolygon) {
                    tempPolygon.setMap(null);
                    setTempPolygon(null);
                  }
                  setViewMode("list");
                  setDrawingMode(false);
                  setSelectedZone(null);
                  setShowNewZoneForm(false);
                }}
                className="bg-gray-600 text-white px-4 py-2 rounded"
              >
                ← Back to List
              </button>

              {selectedZone && (
                <button
                  onClick={() => saveZone(selectedZone._id)}
                  className="bg-blue-600 text-white px-4 py-2 rounded"
                >
                  💾 Save Changes
                </button>
              )}
            </div> */}

            {/* Zone Details */}
            {selectedZone && (
              <div className="p-6 bg-white rounded-lg shadow border space-y-6 mt-10">
                <h3 className="text-xl font-semibold text-gray-800">
                  Editing Zone: {selectedZone.zoneName}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Zone Name
                    </label>
                    <input
                      type="text"
                      value={selectedZone.zoneName}
                      onChange={(e) =>
                        setSelectedZone({
                          ...selectedZone,
                          zoneName: e.target.value,
                        })
                      }
                      className="w-full p-2 border rounded"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Message
                    </label>
                    <input
                      type="text"
                      value={selectedZone.message}
                      onChange={(e) =>
                        setSelectedZone({
                          ...selectedZone,
                          message: e.target.value,
                        })
                      }
                      className="w-full p-2 border rounded"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      color
                    </label>
                    <input
                      type="text"
                      value={selectedZone.color}
                      onChange={(e) =>
                        setSelectedZone({
                          ...selectedZone,
                          color: e.target.value,
                        })
                      }
                      className="w-full p-2 border rounded"
                    />
                  </div>

                  <div className="col-span-1 flex items-center">
                    <input
                      type="checkbox"
                      id="zone-enabled"
                      checked={selectedZone.isEnabled !== false}
                      onChange={(e) =>
                        setSelectedZone({
                          ...selectedZone,
                          isEnabled: e.target.checked,
                        })
                      }
                      className="h-4 w-4"
                    />
                    <label
                      htmlFor="zone-enabled"
                      className="ml-2 text-sm text-gray-700"
                    >
                      Zone Enabled
                    </label>
                  </div>
                </div>

                {/* Points Table */}
                <div className="overflow-x-auto">
                  <table className="w-full table-auto border border-gray-300 text-sm">
                    <thead className="bg-gray-100 text-gray-700">
                      <tr>
                        <th className="p-2 border">#</th>
                        <th className="p-2 border">Name</th>
                        <th className="p-2 border">Latitude</th>
                        <th className="p-2 border">Longitude</th>
                        <th className="p-2 border">Enabled</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editingPoints.map((point, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="p-2 border">{point.sequence}</td>
                          <td className="p-2 border">
                            <input
                              type="text"
                              value={point.name}
                              onChange={(e) =>
                                handlePointChange(index, "name", e.target.value)
                              }
                              className="w-full p-1 border rounded"
                            />
                          </td>
                          <td className="p-2 border">
                            {point.latitude.toFixed(6)}
                          </td>
                          <td className="p-2 border">
                            {point.longitude.toFixed(6)}
                          </td>
                          <td className="p-2 border">
                            <input
                              type="checkbox"
                              checked={point.isEnabled}
                              onChange={(e) =>
                                handlePointChange(
                                  index,
                                  "isEnabled",
                                  e.target.checked
                                )
                              }
                              className="h-4 w-4"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {selectedZone && (
              <button
                onClick={() => saveZone(selectedZone._id)}
                className="bg-blue-600 text-white px-4 py-2 rounded"
              >
                💾 Save Changes
              </button>
            )}
          </div>
        </LoadScript>
      )}
    </div>
  );
};

export default ZoneMap;
